import React from 'react';
import { makeStyles } from '@material-ui/styles';
import { Box, Container, Divider, Grid, Link, Typography } from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import {
  APP_PATH_ABOUT,
  APP_PATH_AI,
  APP_PATH_FAQ_EMPLOYERS,
  APP_PATH_FAQ_JOB_SEEKERS,
  APP_PATH_MESSAGES,
  APP_PATH_MY_APPLICATIONS,
  APP_PATH_PRICING,
  APP_PATH_PRODUCTS,
  APP_PATH_ROOT,
  APP_PATH_SIGNUP,
  APP_PATH_STARTUPS,
  APP_PATH_SUPPORT,
  APP_PATH_TERM_CONDITIONS
} from '../config';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.grey[100],
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(5),
      paddingBottom: theme.spacing(4)
    }
  },
  preFooter: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    marginBottom: theme.spacing(4)
  },
  brandTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1)
  },
  brandBody: {
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
    lineHeight: 1.6
  },
  brandNote: {
    marginTop: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    fontSize: '0.85rem'
  },
  columnTitle: {
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontSize: '0.75rem',
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.primary
  },
  link: {
    display: 'block',
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
    marginBottom: theme.spacing(1),
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
      color: theme.palette.text.primary
    }
  },
  bottomLink: {
    marginBottom: 0
  },
  divider: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(3)
  },
  bottomBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start'
    }
  },
  bottomLinks: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  bottomText: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem'
  }
}));

const Footer = () => {
  const classes = useStyles();

  return (
    <footer className={classes.root}>
      <Container maxWidth="lg">
        <Typography className={classes.preFooter} variant="body2">
          Hiring tools included with every job post. No subscriptions.
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography className={classes.brandTitle} variant="h6">
              Remote Job
            </Typography>
            <Typography className={classes.brandBody} variant="body2">
              A simple hiring platform for remote-first teams. Post jobs, manage
              candidates, and hire globally — all in one place.
            </Typography>
            <Typography className={classes.brandNote} variant="caption">
              Built for modern remote hiring.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography className={classes.columnTitle} variant="subtitle2">
              For Companies
            </Typography>
            {/* TODO: Add /post-a-job route */}
            <Link component={RouterLink} to={APP_PATH_SIGNUP} className={classes.link}>
              Post a Job
            </Link>
            <Link component={RouterLink} to={APP_PATH_PRODUCTS} className={classes.link}>
              Platform Features
            </Link>
            <Link component={RouterLink} to={APP_PATH_PRICING} className={classes.link}>
              Pricing
            </Link>
            <Link component={RouterLink} to={APP_PATH_PRODUCTS} className={classes.link}>
              Hiring Workspace
            </Link>
            <Link component={RouterLink} to={APP_PATH_AI} className={classes.link}>
              AI Tools (Coming Soon)
            </Link>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography className={classes.columnTitle} variant="subtitle2">
              For Candidates
            </Typography>
            {/* TODO: Add /jobs route */}
            <Link
              component={RouterLink}
              to={{ pathname: APP_PATH_ROOT, hash: '#jobs' }}
              className={classes.link}>
              Find Remote Jobs
            </Link>
            {/* TODO: Add /signup route */}
            <Link component={RouterLink} to={APP_PATH_SIGNUP} className={classes.link}>
              Create Free Profile
            </Link>
            <Link
              component={RouterLink}
              to={APP_PATH_MY_APPLICATIONS}
              className={classes.link}>
              My Applications
            </Link>
            <Link component={RouterLink} to={APP_PATH_MESSAGES} className={classes.link}>
              Messages
            </Link>
            <Link
              component={RouterLink}
              to={APP_PATH_FAQ_JOB_SEEKERS}
              className={classes.link}>
              Job Seeker FAQ
            </Link>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography className={classes.columnTitle} variant="subtitle2">
              Company & Resources
            </Typography>
            <Link component={RouterLink} to={APP_PATH_ABOUT} className={classes.link}>
              About
            </Link>
            {/* TODO: Add /community route */}
            <Link component={RouterLink} to={APP_PATH_STARTUPS} className={classes.link}>
              Community
            </Link>
            {/* TODO: Add /faq route */}
            <Link
              component={RouterLink}
              to={APP_PATH_FAQ_EMPLOYERS}
              className={classes.link}>
              FAQ
            </Link>
            <Link component={RouterLink} to={APP_PATH_AI} className={classes.link}>
              What’s Coming (AI Tools)
            </Link>
            {/* TODO: Add /contact route */}
            <Link component={RouterLink} to={APP_PATH_SUPPORT} className={classes.link}>
              Contact
            </Link>
          </Grid>
        </Grid>
        <Divider className={classes.divider} />
        <Box className={classes.bottomBar}>
          <Typography className={classes.bottomText} variant="body2">
            © 2026 Remote Job. All rights reserved.
          </Typography>
          <Box className={classes.bottomLinks}>
            {/* TODO: Add /privacy route */}
            <Link
              component={RouterLink}
              to={APP_PATH_TERM_CONDITIONS}
              className={`${classes.link} ${classes.bottomLink}`}>
              Privacy Policy
            </Link>
            {/* TODO: Add /terms route */}
            <Link
              component={RouterLink}
              to={APP_PATH_TERM_CONDITIONS}
              className={`${classes.link} ${classes.bottomLink}`}>
              Terms of Service
            </Link>
          </Box>
        </Box>
      </Container>
    </footer>
  );
};

export default Footer;
